import { FileUp, FolderUp, Upload } from "lucide-react";
import {
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type FC,
  type ReactNode
} from "react";

import { Button } from "@/components/ui/button";
import {
  collectDroppedFiles,
  collectFileList,
  type CollectedFile
} from "@/features/caseFiles/upload/collectDroppedFiles";
import { useGoogleDrive } from "@/hooks/useGoogleDrive";
import { useSharePoint } from "@/hooks/useSharePoint";
import { cn } from "@/lib/utils";

const SHAREPOINT_LOGO_URL =
  "https://cdn.jsdelivr.net/gh/glincker/thesvg@main/public/icons/microsoft-sharepoint/default.svg";

const GOOGLE_DRIVE_LOGO_URL =
  "https://cdn.jsdelivr.net/gh/glincker/thesvg@main/public/icons/google-drive-2026/default.svg";

/** Three tilted document cards with an upload glyph on the front one. */
const StackedDocuments: FC = () => (
  <div className="relative h-14.5 w-20.5" aria-hidden>
    <div className="absolute top-1.75 left-0.75 size-12.5 -rotate-8 rounded-2xl bg-elevated shadow-sm ring-1 ring-line-2" />
    <div className="absolute top-1.75 right-0.75 size-12.5 rotate-8 rounded-2xl bg-elevated shadow-sm ring-1 ring-line-2" />
    <div className="absolute top-0.75 left-4 flex h-13 w-12.5 items-center justify-center rounded-2xl bg-elevated shadow-md ring-1 ring-line-2">
      <FileUp className="size-5.5 text-fg-3" strokeWidth={2} />
    </div>
  </div>
);

/** A source button in the "Or choose from" row (SharePoint, Google Drive, a case). */
export const UploadSourceButton: FC<{
  icon: ReactNode;
  label: string;
  disabled?: boolean;
  onClick: () => void;
  testId?: string;
}> = ({ icon, label, disabled, onClick, testId }) => (
  <Button
    variant="secondary"
    size="md"
    className="min-w-36"
    disabled={disabled}
    onClick={onClick}
    data-testid={testId}
  >
    {icon}
    {label}
  </Button>
);

type Props = {
  onFiles: (collected: CollectedFile[]) => void;
  disabled?: boolean;
  /** The accepted-types line under the heading. */
  hint?: string;
  /** Extensions the file picker offers (the drop takes anything). */
  accept?: readonly string[];
  /** More sources after SharePoint and Google Drive. */
  extraSources?: ReactNode;
  /** Off: the picker takes one file (a drop or a source can still bring more). */
  multiple?: boolean;
  /** Off: no Upload Folder, for hosts that don't keep folder paths. */
  folders?: boolean;
};

/**
 * The uploader card: the whole surface is a drop target; Upload picks files,
 * Upload Folder picks a folder, and the source buttons bring files from
 * SharePoint, Google Drive or anything the host adds. It only collects files;
 * the dialog decides what happens to them.
 */
export const UploadSurface: FC<Props> = ({
  onFiles,
  disabled = false,
  hint = "PDF, DOCX, XLSX, PPTX, PNG or JPG",
  accept,
  extraSources,
  multiple = true,
  folders = true
}) => {
  const [dragActive, setDragActive] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const { pickItems: pickGoogleDrive } = useGoogleDrive();
  const { pickItems: pickSharePoint } = useSharePoint();

  const onInput = (e: ChangeEvent<HTMLInputElement>) => {
    onFiles(collectFileList(e.target.files));
    e.target.value = "";
  };
  const onDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (disabled || !e.dataTransfer?.items?.length) return;
    onFiles(await collectDroppedFiles(e.dataTransfer));
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragActive(true);
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={onDrop}
      className={cn(
        "flex min-h-75 flex-col items-center justify-center gap-6 rounded-2xl border border-dotted bg-elevated px-6 py-8 text-center transition-[background-color,border-color]",
        dragActive
          ? "border-accent-line bg-accent-soft"
          : "border-line-3 hover:bg-fill-1"
      )}
      data-testid="upload-surface"
      data-drag-active={dragActive || undefined}
    >
      <StackedDocuments />
      <div className="space-y-1">
        <p className="text-md text-fg">
          Drag and drop your {multiple ? "files" : "file"}
        </p>
        <p className="text-sm text-fg-3" data-testid="upload-surface-hint">
          {hint}
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          variant="primary"
          size="md"
          disabled={disabled}
          onClick={() => fileInput.current?.click()}
          data-testid="upload-surface-files"
        >
          <Upload /> Upload
        </Button>
        {folders ? (
          <Button
            variant="secondary"
            size="md"
            disabled={disabled}
            onClick={() => folderInput.current?.click()}
            data-testid="upload-surface-folder"
          >
            <FolderUp /> Upload Folder
          </Button>
        ) : null}
      </div>
      <div className="w-full space-y-3">
        <p className="text-sm text-fg-3">Or choose from</p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <UploadSourceButton
            icon={<img src={SHAREPOINT_LOGO_URL} alt="" className="size-4" />}
            label="SharePoint"
            disabled={disabled}
            onClick={async () => {
              const items = await pickSharePoint();
              if (items.length) onFiles(items);
            }}
          />
          <UploadSourceButton
            icon={<img src={GOOGLE_DRIVE_LOGO_URL} alt="" className="size-4" />}
            label="Google Drive"
            disabled={disabled}
            onClick={async () => {
              const items = await pickGoogleDrive();
              if (items.length) onFiles(items);
            }}
          />
          {extraSources}
        </div>
      </div>

      <input
        ref={fileInput}
        type="file"
        multiple={multiple}
        hidden
        accept={accept?.join(",")}
        onChange={onInput}
        aria-label={multiple ? "Choose files" : "Choose a file"}
        data-testid="upload-dialog-file-input"
      />
      {folders ? (
        <input
          ref={folderInput}
          type="file"
          hidden
          onChange={onInput}
          aria-label="Choose a folder"
          data-testid="upload-dialog-folder-input"
          {...({ webkitdirectory: "", directory: "" } as Record<
            string,
            string
          >)}
        />
      ) : null}
    </div>
  );
};
